/**
 * Shared API client scaffolding.
 *
 * Requests go through the Athena API gateway (platform-api), which proxies
 * `<base>/underwriting-exceptions/*` to this product's backend. Three things
 * about that path are load-bearing:
 *
 *  - **axios, not fetch.** The Keycloak bearer is attached by a single global
 *    axios interceptor in `src/index.js`. `fetch` never sees it, so a fetch-based
 *    client arrives unauthenticated and the gateway rejects it.
 *  - **the base URL comes from `config/server.js`**, not a hardcoded "/api", so
 *    local dev against `http://localhost:9000/` works as well as the deployed
 *    origin. Note the trailing slash it already carries.
 *  - **the `underwriting-exceptions/` slug is required.** Without it a request
 *    lands on the gateway root and matches no proxy route.
 */
import axios from "axios";

const serverConfig = require("../../../../config/server.js");

const BASE: string = serverConfig.api_base_url;

/** Gateway slug this product is proxied under. */
const UE_PREFIX = "underwriting-exceptions/";

/**
 * Endpoints with a real backend today. Everything else still resolves from its
 * mock generator.
 *
 * This list exists because mock-vs-live is not one switch: pages 1 and 2 are
 * served by `ue-exceptions-and-turnaround-time`, while pages 3-7 have no backend
 * at all and no source tables behind them yet. A single global flag would take
 * all seven live at once and five of them would render an error card. Add a path
 * here when its endpoint actually exists.
 */
const LIVE_PATHS = new Set([
  // Page 1 — Underwriting Health
  "/health-summary",
  "/office-mapping",
  // Page 2 — Productivity
  "/productivity-summary",
  "/productivity-projection",
  "/automation-readiness",
  // Page 3 — Exceptions and Overrides. Partially suppressed: override rate and agreement
  // rate come back as zeros with an entry in `caveats` explaining why, because
  // athena_recommendation is a 50/50 coin flip. notRatedRate, reconciliation and
  // exceptionsByTier are real.
  "/exceptions-overrides/summary",
  // Page 4 — Policy Compliance by Rule. All 13 rules detect; read `detectionStatus`
  // before trusting a count, since 4 of them are floors rather than complete counts.
  "/policy-compliance/summary",
  // Page 5 — Deal-Level Exceptions Log, and its reconcile mutation.
  "/exceptions-log/summary",
  // Page 6 — Dealer and Channel Quality. Fully real except `overrideRatePct`, which is null.
  "/dealer-quality",
  // Page 7 — Agreement Rate and Credit Culture. Serves APPROVAL rate; read `metric`.
  "/agreement-culture",
  // Manage Alerts (sub-step 1a).
  "/alert-rules",
]);

/**
 * Live paths matched by prefix rather than exact string, for routes carrying an id.
 *
 * `mockFor` takes the literal path a caller is about to request, which for a mutation
 * includes the id — so `/exceptions-log/41562416/reconcile` can never be in the exact
 * set above. Kept separate from LIVE_PATHS so the exact set stays a readable inventory
 * of endpoints rather than a mix of paths and patterns.
 */
const LIVE_PREFIXES = ["/exceptions-log/", "/alert-rules/"];

/** Explicit opt-out for the whole product, for demoing without a backend. */
const forceMock = process.env.REACT_APP_DATA_MODE === "mock";

/** Should this specific endpoint resolve from its mock generator? */
export function mockFor(path: string): boolean {
  if (forceMock) return true;
  if (LIVE_PATHS.has(path)) return false;
  return !LIVE_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/** Simulated network latency for mock responses (ms). */
const MOCK_LATENCY = 260;

export function mockResponse<T>(data: T, latency = MOCK_LATENCY): Promise<T> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(data), latency);
  });
}

function url(path: string, params?: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value != null) search.set(key, value);
  }
  const query = search.toString();
  return `${BASE}${UE_PREFIX}${path.replace(/^\//, "")}${query ? `?${query}` : ""}`;
}

export async function apiGet<T>(path: string, params?: Record<string, string | undefined>) {
  try {
    const response = await axios.get<T>(url(path, params));
    return response.data;
  } catch (error: any) {
    // Preserve the message shape the pages already render, so an error card reads
    // the same whether the failure came from the gateway or the backend.
    const status = error?.response?.status ?? "network";
    throw new Error(`Request failed (${status}): ${path}`);
  }
}

export async function apiPatch<T>(path: string, body: unknown) {
  try {
    const response = await axios.patch<T>(url(path), body);
    return response.data;
  } catch (error: any) {
    const status = error?.response?.status ?? "network";
    // A 400 from the backend carries a reason worth showing — the rollout dialog
    // surfaces it directly rather than saying "something went wrong".
    const detail = error?.response?.data?.detail;
    throw new Error(detail ? String(detail) : `Request failed (${status}): ${path}`);
  }
}

/**
 * POST, for the three mutations that are not partial updates: reconciling an
 * exception, proposing an alert rule, and reviewing a proposal.
 *
 * Surfaces `detail` verbatim exactly as apiPatch does, and that matters more here
 * than it does for a PATCH: these endpoints answer with reasons a user needs to
 * read rather than retry past — 400 "A reconciliation note is required", 403 "A
 * proposal cannot be reviewed by the person who raised it", 409 "Proposal PRP-101
 * is already APPROVED". A generic "something went wrong" would send someone
 * hunting for a bug that is not there.
 */
export async function apiPost<T>(path: string, body: unknown) {
  try {
    const response = await axios.post<T>(url(path), body);
    return response.data;
  } catch (error: any) {
    const status = error?.response?.status ?? "network";
    const detail = error?.response?.data?.detail;
    throw new Error(detail ? String(detail) : `Request failed (${status}): ${path}`);
  }
}

/** Stable query key helper so hooks stay consistent across pages. */
export function queryKey(scope: string, params?: Record<string, unknown>) {
  return params ? ([scope, params] as const) : ([scope] as const);
}
